import { useEffect } from 'react';
import {
  HouseholdStatus,
  iOSAppNewestReleasedVersion,
  PaymentProcessor,
  StripeSubscriptionLevel,
} from '../../../common/src/model.ts';
import { createStripeCheckoutSession } from '../../../common/src/server_api.ts';
import { getUnknownHouseholdCredentials } from '../../../common/src/util.ts';
import { localStorageGet, LocalStorageKey, localStorageRemove, localStorageSet } from '../data/client_local_storage.ts';
import { useLocalStorage } from '../data/use_local_storage.tsx';
import {
  compareAppVersions,
  ContainingAppPlatform,
  getContainingApp,
  isLegacyGooglePlayAppUserAgent,
} from './containing_app.ts';
import { Device, getDevice, getPlatform, Platform } from './platform.ts';

const AppleSubscriptionInfoTimeoutMS = 10000;

export const IOSAppVersionWithPushNotifications = '4.0';

export enum ProductId {
  HouseholdAnnualSubscription = 'com.crescendosw.ensemble.household.annual',
  HouseholdMonthlySubscription = 'com.crescendosw.ensemble.household.monthly',
}

// Launches Stripe Checkout for a household subscription (GitHub issue #3096). The server creates
// the Checkout Session (stamping the householdToken into the subscription metadata so the webhook
// can link the purchase server-side, and prefilling the household email) and returns the session
// URL; the client just navigates to it. Throws if the checkout session cannot be created.
export async function launchStripeCheckout({
  householdToken,
  level,
  promo,
  fetchCheckoutUrl = (params) => createStripeCheckoutSession(params),
  isMobileApp = isInsideMobileApp,
  openExternal = (url: string) => window.open(url, '_blank', 'noopener,noreferrer'),
  navigateInPlace = (url: string) => window.location.assign(url),
}: {
  householdToken: string;
  level: StripeSubscriptionLevel;
  // promotional campaign slug from the landing URL (GitHub issue #3551)
  promo?: string;
  fetchCheckoutUrl?: (params: {
    householdToken: string;
    level: StripeSubscriptionLevel;
    promo?: string;
  }) => Promise<string>;
  isMobileApp?: () => boolean;
  openExternal?: (url: string) => Window | null | void;
  navigateInPlace?: (url: string) => void;
}) {
  const url = await fetchCheckoutUrl({ householdToken, level, ...(promo && { promo }) });
  if (isMobileApp()) {
    openExternal(url);
    return;
  }
  navigateInPlace(url);
}

export const AppStoreUrl = 'https://apps.apple.com/us/app/sing-your-part/id1483413949?itsct=apps_box_link&itscg=30200';
export const GooglePlayUrl = 'https://play.google.com/store/apps/details?id=singyourpart.app.twa';

// Apple product IDs
export enum AppleProductId {
  Annual = 'com.crescendosw.ensemble.household.annual',
  Monthly = 'com.crescendosw.ensemble.household.monthly',
  AnnualDonationLevel1 = '100.Annual.Gift.Subscription',
  AnnualDonationLevel2 = '250.annual.gift.subscription',
}

export function isInChromeOnIOS(): boolean {
  return navigator.userAgent.includes('CriOS');
}

export function isInsideAppleAppStoreApp(): boolean {
  if (getContainingApp()?.platform === ContainingAppPlatform.iOS) {
    return true;
  }
  // TODO(remove after 2027-10-13): iOS app versions 4.x predate the user agent token and are
  // recognized by their WebKit message handlers instead; the Learn update screen (learn_visibility.ts)
  // depends on it, so delete them together
  return Boolean(window.webkit) && !isInChromeOnIOS();
}

export function isInsideGooglePlayApp(): boolean {
  if (getContainingApp()?.platform === ContainingAppPlatform.Android) {
    return true;
  }
  // TODO(remove after 2027-10-13): Google Play app versions 23 and earlier sent the bare user agent
  // "Sing Your Part", and the "wv" check recognized a stock WebView user agent; the Learn update
  // screen (learn_visibility.ts) depends on it, so delete them together
  const userAgent = navigator.userAgent?.toLowerCase() ?? '';
  return (
    isLegacyGooglePlayAppUserAgent(userAgent) ||
    (userAgent.includes('android') && !!userAgent.match(/[^a-zA-Z]wv[^a-zA-Z]/))
  );
}

export function isInsideMobileApp(): boolean {
  return isInsideAppleAppStoreApp() || isInsideGooglePlayApp();
}

export function doesAppVersionSupportSubscriptions(): boolean {
  return Boolean(window.webkit?.messageHandlers?.subscribe);
}

export function isWKWebView(): boolean {
  if (window.webkit?.messageHandlers?.subscribe) {
    return true;
  }
  return false;
}

// The containing app's platform and version: from the user agent token (store apps 24.0 and later), else
// from the deprecated getAppVersion reply that iOS 4.x posts into local storage. Undefined in a browser
// and in an Android app older than 24.0, which never reported its version.
export function useContainingAppVersion(): { platform: ContainingAppPlatform; version: string } | undefined {
  // TODO(remove after 2026-12-18): legacy iOS 4.x version fallback; delete along with
  // LocalStorageKey.IOSAppVersion
  const [legacyIOSAppVersion] = useLocalStorage(LocalStorageKey.IOSAppVersion);
  const containingApp = getContainingApp();
  if (containingApp) {
    return { platform: containingApp.platform, version: containingApp.appVersion };
  }
  if (legacyIOSAppVersion) {
    return { platform: ContainingAppPlatform.iOS, version: legacyIOSAppVersion };
  }
  return undefined;
}

export function useAppUpdateNeeded(): boolean {
  const containingAppVersion = useContainingAppVersion();
  return (
    isInsideAppleAppStoreApp() &&
    (!containingAppVersion || compareAppVersions(containingAppVersion.version, iOSAppNewestReleasedVersion) < 0)
  );
}

export function getMonthlySubscriptionPrice() {
  return 4.99;
}

export function getAnnualSubscriptionPrice() {
  return 49.99;
}

export function isInsideMobileBrowser() {
  return getPlatform() === Platform.Mobile && !isInsideMobileApp();
}

export function isMobile() {
  return isInsideMobileBrowser() || isInsideMobileApp();
}

export interface AppleSubscriptionInfo {
  appAccountToken?: string;
  productId?: string;
  expirationDate?: string;
}

// sets up a callback on globalThis that is used by the iOS app to forward subscription info
export function useSetupIOSSubscriptionInfoCallback(callback: (subscriptionInfo: AppleSubscriptionInfo) => void) {
  useEffect(() => {
    globalThis.receiveSubscriptionInfo = (subscriptionInfo: AppleSubscriptionInfo) => {
      callback(subscriptionInfo);
    };
    return () => {
      delete (globalThis as any).receiveSubscriptionInfo;
    };
  }, [callback]);
}

// sets up a callback on globalThis that is used by the iOS app to forward subscription info
export function useSetupIOSCanMakePurchasesCallback(callback: (canMakePurchases: boolean) => void) {
  useEffect(() => {
    globalThis.receiveCanMakePurchases = (canMakePurchases: boolean) => {
      callback(canMakePurchases);
    };
    return () => {
      delete (globalThis as any).receiveCanMakePurchases;
    };
  }, [callback]);
}

// iOS app 4.17+ returns the answer via WKScriptMessageHandlerWithReply (postMessage returns
// a Promise). Older builds have no reply and instead push back via globalThis.receiveCanMakePurchases.
// We register the legacy callback unconditionally so either path resolves the same promise.
// TODO(remove after 2027-01-01): once no old iOS apps are in the wild, simplify to
// `return await handler.postMessage({})` and drop the legacy globalThis.receiveCanMakePurchases path.
export function requestAppleCanMakePurchases(): Promise<boolean | undefined> {
  const handler = window.webkit?.messageHandlers?.requestCanMakePurchases;
  if (!handler) return Promise.resolve(undefined);
  return new Promise((resolve) => {
    globalThis.receiveCanMakePurchases = resolve;
    const reply = handler.postMessage({}) as Promise<boolean> | undefined;
    if (reply && typeof reply.then === 'function') {
      void reply.then(resolve);
    }
  });
}

// See requestAppleCanMakePurchases for the dual-path explanation. Subscription info has an
// extra wrinkle: old iOS apps also push out-of-band (post-purchase, from Billing.purchaseProduct)
// to whoever is currently registered as globalThis.receiveSubscriptionInfo. We chain to any
// previously-installed handler so polling doesn't clobber a persistent listener (e.g.
// the post-purchase listener in subscribe_payment.tsx).
// TODO(remove after 2027-01-01): once no old iOS apps are in the wild, simplify to
// `return await handler.postMessage({})` and drop the chained-handler wrapper around
// globalThis.receiveSubscriptionInfo. The persistent listener in subscribe_payment.tsx can
// also be deleted at that point.
export function requestAppleSubscriptionInfo(): Promise<AppleSubscriptionInfo | undefined> {
  const handler = window.webkit?.messageHandlers?.requestSubscriptionInfo;
  if (!handler) return Promise.resolve(undefined);
  return new Promise((resolve) => {
    const previous = globalThis.receiveSubscriptionInfo;
    let resolved = false;
    const settle = (info: AppleSubscriptionInfo | undefined) => {
      if (resolved) return;
      resolved = true;
      globalThis.receiveSubscriptionInfo = previous;
      resolve(info);
    };
    // Old iOS pushes via globalThis.receiveSubscriptionInfo. Forward to the previous handler
    // (the persistent post-purchase listener, if any) so out-of-band pushes still propagate,
    // then resolve our own promise.
    globalThis.receiveSubscriptionInfo = (info: AppleSubscriptionInfo) => {
      previous?.(info);
      settle(info);
    };
    const reply = handler.postMessage({}) as Promise<AppleSubscriptionInfo> | undefined;
    if (reply && typeof reply.then === 'function') {
      // New iOS reply path: resolve directly (Swift doesn't push, so no need to forward
      // to `previous` here — that listener exists only for the legacy push).
      void reply.then(settle);
    }
  });
}

// Polls Apple subscription info until expirationDate appears or until timeout.
// Used after a purchase initiated by the webapp: there is no Swift→JS push channel under
// iOS 18 with limitsNavigationsToAppBoundDomains = true, so the webapp pulls instead.
export async function pollForAppleSubscriptionInfo({
  intervalMs = 1000,
  timeoutMs = 10000,
}: { intervalMs?: number; timeoutMs?: number } = {}): Promise<AppleSubscriptionInfo | undefined> {
  const deadline = Date.now() + timeoutMs;
  while (Date.now() < deadline) {
    const info = await requestAppleSubscriptionInfo();
    if (info?.expirationDate) {
      return info;
    }
    await new Promise((res) => setTimeout(res, intervalMs));
  }
  return undefined;
}

export function subscribeApple(productId: ProductId, householdToken: string) {
  window.webkit?.messageHandlers?.subscribe?.postMessage({ productId, householdToken });
}

export function manageAppleSubscription() {
  window.webkit?.messageHandlers?.manageSubscription?.postMessage({});
}

// Navigates to the Stripe billing portal, except on iOS where we bring up
// Apple's subscription management UI instead (formerly the Manage Subscription button)
export function manageSubscription() {
  if (isInsideAppleAppStoreApp()) {
    manageAppleSubscription();
    return;
  }
  window.location.href = 'https://billing.stripe.com/p/login/aEU8zfeTE1LU5l6aEE';
}

// should only be called from synchronizeHousehold
export async function synchronizeMobileHouseholdStatus() {
  try {
    const household = localStorageGet(LocalStorageKey.Household);

    if (isInsideMobileApp()) {
      await synchronizeClientSubscription();
    }

    if (isInsideAppleAppStoreApp()) {
      const clientSubscription = localStorageGet(LocalStorageKey.ClientSubscription);
      if (
        clientSubscription?.subscriptionExpirationTimestamp &&
        clientSubscription.subscriptionExpirationTimestamp > Date.now()
      ) {
        // prefer the household token associated with the iCloud account
        // automatically configures new devices to be part of a subscribed household
        const token = clientSubscription?.appAccountToken ?? household?.token;
        if (token) {
          localStorageSet(LocalStorageKey.Household, {
            ...(household ? household : getUnknownHouseholdCredentials(token)),
            token,
            status: HouseholdStatus.Subscribed,
            orgIds: [],
          });
        }
        return;
      }
    }
  } catch (e: any) {
    console.error(`Failed to sync mobile household status: ${e.message}`);
  }
}

// TODO(remove after 2026-12-18): stores the iOS 4.x getAppVersion reply; delete with
// requestIOSAppVersion
function storeIOSAppVersion(appVersion: string | number) {
  localStorageSet(LocalStorageKey.IOSAppVersion, String(appVersion));
}

/**
 * @deprecated remove after 2026-12-18: delete together with receiveAppVersion,
 * LocalStorageKey.IOSAppVersion and coerceIOSAppVersionToString. iOS app versions 4.x report their
 * version only through this reply; from 24.0 the user agent token carries it (see containing_app.ts),
 * so this returns at once when the token is present.
 */
export function requestIOSAppVersion() {
  if (getContainingApp() || !isInsideAppleAppStoreApp()) return;
  const handler = window.webkit?.messageHandlers?.getAppVersion;
  if (!handler) return;

  // Pre-iOS-18 path: older iOS app builds call receiveAppVersion via
  // evaluateJavaScript. Kept for users who haven't updated the native app yet.
  globalThis.receiveAppVersion = ({ appVersion }: { appVersion: string | number }) => storeIOSAppVersion(appVersion);

  // iOS app 4.16+ path: getAppVersion is registered as a
  // WKScriptMessageHandlerWithReply, so postMessage returns a Promise that
  // resolves with the version string. This sidesteps the content-world
  // isolation that blocks the evaluateJavaScript callback on iOS 18 with
  // limitsNavigationsToAppBoundDomains. Older iOS apps return undefined; check
  // for .then so we don't crash there.
  const reply = handler.postMessage({}) as Promise<string | number> | undefined;
  if (reply && typeof reply.then === 'function') {
    void reply.then(storeIOSAppVersion);
  }
}

// retrieves the client subscription info & stores it
// should only be called from synchronizeMobileHouseholdStatus
async function synchronizeClientSubscription() {
  if (!isInsideAppleAppStoreApp()) {
    return;
  }

  const appleSubscriptionInfo = await Promise.race([
    requestAppleSubscriptionInfo(),
    new Promise<undefined>((res) => setTimeout(() => res(undefined), AppleSubscriptionInfoTimeoutMS)),
  ]);
  delete (globalThis as any).receiveSubscriptionInfo;

  if (!appleSubscriptionInfo?.expirationDate) {
    localStorageRemove(LocalStorageKey.ClientSubscription);
    return;
  }

  const subscription = {
    paymentProcessor: PaymentProcessor.Apple,
    appAccountToken: appleSubscriptionInfo.appAccountToken,
    subscriptionExpirationTimestamp: parseAppleDateAsTimestamp(appleSubscriptionInfo.expirationDate),
    productId: appleSubscriptionInfo.productId,
  };
  localStorageSet(LocalStorageKey.ClientSubscription, subscription);
}

// date string has format "2024-09-27 18:24:42 +0000"
export function parseAppleDateAsTimestamp(dateString?: string): number | undefined {
  if (!dateString) {
    return;
  }
  const match = dateString.match(/^(?<year>\d+)-(?<month>\d+)-(?<day>\d+) .*/);
  if (!match || !match.groups) {
    console.error(`Could not parse Apple date "${dateString}"`);
    return;
  }
  const { year, month, day } = match.groups;
  return new Date(Number(year), Number(month) - 1, Number(day)).getTime();
}

export function getAppStoreUrl(): string | undefined {
  if (getDevice() === Device.iOS) {
    return AppStoreUrl;
  } else if (getDevice() === Device.Android) {
    return GooglePlayUrl;
  }
}
